import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Address } from '@vsolv/core/address/domain';
import { UserService } from '@vsolv/core/users/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Asset } from '@wsphere/assets/domain';
import { BatchPaymentService, describeBatchPaymentError, PaidBatchPayment } from '../../services';

type Option = { name: string; abbreviation: string };

// Asset.COUNTRIES only names 'Canada' and 'USA'.
const COUNTRY_OPTIONS: Option[] = [...Asset.COUNTRIES, { name: 'United States', abbreviation: 'US' }];

function toAbbreviation(options: readonly Option[], value: string | null) {
  const needle = value?.trim().toLowerCase();
  if (!needle) return '';

  return (
    options.find(option => option.abbreviation.toLowerCase() === needle)?.abbreviation ??
    options.find(option => option.name.toLowerCase() === needle)?.abbreviation ??
    ''
  );
}

/**
 * The distributor form stores country and state as free text; vs-address-input keys its dropdowns by
 * abbreviation and drops the rest. Map what resolves, blank what doesn't — never guess a state.
 */
function toAddressInputValue(address: Address.Model | null): Address.Model | null {
  if (!address) return null;

  const country = toAbbreviation(COUNTRY_OPTIONS, address.country);

  // validateState() rejects every state while country is empty, so there is nothing to resolve.
  const states = country === 'CA' ? Asset.CA_PROVINCES : country === 'US' ? Asset.US_STATES : [];

  return { ...address, country, state: toAbbreviation(states, address.state) };
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-pay-batch-dialog',
  templateUrl: './pay-batch.dialog.html',
  standalone: false,
})
export class PayBatchDialog {
  private toastSvc = inject(ToastService);
  private userSvc = inject(UserService);
  private batchSvc = inject(BatchPaymentService);
  private formBuilder = inject(FormBuilder);

  @ViewChild(DialogComponent) private dialog?: DialogComponent;
  @ViewChild('input') protected paymentInput?: { _state: 'select' | 'credit-card' | 'bank' };

  @Input() batchPaymentId: string | null = null;
  @Input() warrantyCount = 0;
  /** Total owed, in cents. Displayed only — the server charges its own sum of the stored snapshots. */
  @Input() amount = 0;

  /** Seeds the billing address from the seller's record. A default only — the field stays required. */
  @Input() defaultBillingAddress: Address.Model | null = null;

  /**
   * Emitted on every close, carrying whether the charge went through. The page has to re-read either
   * way: paying claims the batch as SUBMITTED before it charges, and that claim survives a failed
   * charge, so a dismissed failure leaves the page holding a batch the server no longer calls a draft.
   */
  @Output() readonly closed = new EventEmitter<boolean>();

  // The picker lists this user's saved methods and the API bills the same user, so they must match.
  protected currentUser$ = this.userSvc.getSelf();

  protected readonly form = this.formBuilder.group({
    paymentMethodId: [null as string | null, Validators.required],
    billingAddress: [null as Address.Model | null, Validators.required],
  });

  protected receipt: PaidBatchPayment | null = null;
  protected paying = false;
  protected gatewayInstanceId: string | null = null;
  protected loadingGateway = false;
  protected gatewayError: string | null = null;

  open() {
    this.form.reset();
    // Re-seeded on every open, since reset() clears it.
    this.form.patchValue({ billingAddress: toAddressInputValue(this.defaultBillingAddress) });

    this.receipt = null;
    this.paying = false;
    this.gatewayInstanceId = null;
    this.gatewayError = null;
    void this.loadGateway();

    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  protected async pay() {
    const { paymentMethodId, billingAddress } = this.form.value;
    if (!this.batchPaymentId || !this.gatewayInstanceId || !paymentMethodId || !billingAddress || this.paying) return;

    this.paying = true;

    try {
      this.receipt = await this.batchSvc.pay(this.batchPaymentId, paymentMethodId, billingAddress);
    } catch (error) {
      console.error(error);

      // The batch stays locked and keeps its payment, so submitting again is a retry, not a new charge.
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to complete payment',
        text: describeBatchPaymentError(error, 'The payment could not be completed. Try a different payment method.'),
      });
      this.form.controls.paymentMethodId.reset();
      await this.loadGateway();
    } finally {
      this.paying = false;
    }
  }

  private async loadGateway(): Promise<void> {
    if (!this.batchPaymentId) return;
    this.loadingGateway = true;
    this.gatewayError = null;
    try {
      this.gatewayInstanceId = (await this.batchSvc.retrieveGateway(this.batchPaymentId)).gatewayInstanceId;
    } catch (error) {
      this.gatewayInstanceId = null;
      this.gatewayError = describeBatchPaymentError(error, 'Unable to determine the gateway for this batch.');
    } finally {
      this.loadingGateway = false;
    }
  }

  protected done() {
    this.close();
  }

  // Bound to the dialog's own close so the X, the backdrop and Escape all refresh the page too.
  // Clearing the receipt first keeps it to one emit per payment.
  protected onClosed() {
    const wasPaid = !!this.receipt;

    this.receipt = null;
    this.closed.emit(wasPaid);
  }
}
